import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/en';
import { translations as arTranslations } from '../translations/ar';
import api from '../config/api';
import { formatPriceEGP, formatNumber, formatPhoneNumber, toArabicNumerals } from '../utils/formatting';
import useContactSettings from '../hooks/useContactSettings';
import { getFreeShippingThreshold } from '../services/shippingService';
import { isPushSupported, subscribeToPush } from '../pwa/PushNotifications';
import './Header.css';

const Header = () => {
  const { getCartItemCount, getCartTotal } = useCart();
  const { language, changeLanguage } = useLanguage();
  const { contactSettings } = useContactSettings();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [announcementTexts, setAnnouncementTexts] = useState([]);
  const [announcementEnabled, setAnnouncementEnabled] = useState(true);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(true);
  const cartItemCount = getCartItemCount();
  const cartTotal = getCartTotal();
  const location = useLocation();
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  const t = language === 'ar' ? arTranslations : translations;

  useEffect(() => {
    if (isStandalone) return;
    const handler = (e) => { e.preventDefault(); setDeferredInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone]);

  useEffect(() => {
    if (!isStandalone || !isPushSupported()) return;
    const { isSubscribed } = require('../pwa/PushNotifications');
    isSubscribed().then(sub => setPushSubscribed(sub));
  }, [isStandalone]);

  const handleInstallApp = useCallback(async () => {
    if (isPushSupported()) subscribeToPush('client').catch(() => {});
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') setDeferredInstallPrompt(null);
    } else {
      setShowInstallGuide(true);
    }
  }, [deferredInstallPrompt]);
  
  // Use dynamic contact settings or fallback to defaults
  const whatsappNumber = (contactSettings?.whatsapp || '+20 12 29009073').replace(/\D/g, '');
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;
  const phoneNumber = contactSettings?.phone || '+20 12 29009073';
  const email = contactSettings?.email || 'mohamedhassanamin.co@gmail.com';
  const locationAddress = contactSettings?.location_address || '9 Abdel Hakk El Sonbati St., Opera Sq- Cairo - Egypt';
  const locationMapUrl = contactSettings?.location_map_url || 'https://maps.app.goo.gl/eLfVkqokJQxiXmyY8?g_st=iw';
  const instagramUrl = contactSettings?.instagram_url || '';
  const facebookUrl = contactSettings?.facebook_url || 'https://www.facebook.com';
  const creditName = contactSettings?.credit_name || 'Ektra Tech';
  const creditNameAr = contactSettings?.credit_name_ar || contactSettings?.credit_name || 'إكترا';
  const creditUrl = contactSettings?.credit_url || 'https://ektra-eg.com';

  // Fetch announcement settings
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await api.get('/site-settings/announcement');
        const data = response.data;
        setAnnouncementEnabled(data.enabled);
        if (data.texts && data.texts.length > 0) {
          // Separate English and Arabic texts, filtering out empty/whitespace-only values
          const enTexts = data.texts
            .filter(t => !t.key.endsWith('_ar'))
            .map(t => t.value)
            .filter(v => v && v.trim().length > 0);
          const arTexts = data.texts
            .filter(t => t.key.endsWith('_ar'))
            .map(t => t.value)
            .filter(v => v && v.trim().length > 0);
          
          // Use Arabic texts if language is Arabic and Arabic texts exist
          if (language === 'ar' && arTexts.length > 0) {
            setAnnouncementTexts(arTexts);
          } else {
            setAnnouncementTexts(enTexts);
          }
        }
      } catch (error) {
        // Use fallback texts on error. The free-shipping line comes from the
        // shipping-settings API when available; never a hardcoded amount.
        const threshold = await getFreeShippingThreshold();
        const shippingText = threshold
          ? t.freeShippingOverAmount.replace('{amount}', formatNumber(threshold, language))
          : t.freeShippingGeneric;
        setAnnouncementTexts([shippingText, t.welcomeMessage]);
      }
    };
    fetchAnnouncement();
  }, [t.freeShippingOverAmount, t.freeShippingGeneric, t.welcomeMessage, language]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 36);
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMobileMenuToggle = (e) => {
    e.preventDefault();
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setLanguageMenuOpen(false);
  }, [location.pathname]);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuOpen && !event.target.closest('.language-menu-wrapper')) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [languageMenuOpen]);

  // Add overlay class to body when mobile menu is open
  useEffect(() => {
    const mainWrapper = document.querySelector('.main-wrapper');
    if (mobileMenuOpen) {
      mainWrapper?.classList.add('overlay-active');
    } else {
      mainWrapper?.classList.remove('overlay-active');
    }
    return () => {
      mainWrapper?.classList.remove('overlay-active');
    };
  }, [mobileMenuOpen]);

  const headerClasses = `header-area sticky-bar pt-10 ${isScrolled ? 'stick' : ''}`;

  // Helper function to convert numbers in text to Arabic numerals
  const convertTextNumbers = (text) => {
    if (language === 'ar') {
      return toArabicNumerals(text);
    }
    return text;
  };

  return (
    <>
      {/* Top Announcement Bar - Infinite Scrolling */}
      {announcementEnabled && announcementTexts.length > 0 && (
        <div className="top-announcement-bar">
          <div className="announcement-content">
            <div className={`announcement-text ${language === 'ar' ? 'scroll-rtl' : 'scroll-ltr'}`}>
              {/* Even number of repeats for seamless -50% loop */}
              {[...Array(8)].map((_, repeatIndex) => (
                <React.Fragment key={repeatIndex}>
                  {announcementTexts.length > 0 ? (
                    announcementTexts.map((text, index) => {
                      const trimmedText = text?.trim();
                      if (!trimmedText) return null;
                      return (
                        <React.Fragment key={`${repeatIndex}-${index}`}>
                          <span>{convertTextNumbers(trimmedText)}</span>
                          <span className="separator">•</span>
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <>
                      <span>{convertTextNumbers(t.freeShippingGeneric)}</span>
                      <span className="separator">•</span>
                      <span>{convertTextNumbers(t.welcomeMessage)}</span>
                      <span className="separator">•</span>
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
      
      <header className={headerClasses} style={{ backgroundColor: '#ffffff' }}>
        <div className="main-header-wrap">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-xl-2 col-lg-3 col-md-6 col-6">
                <div className="logo">
                  <Link to="/">
                    <img
                      src="/assets/images/logo/logo-main.png"
                      alt="Logo"
                      width="120"
                      height="35"
                      style={{
                        maxHeight: '35px',
                        opacity: 1,
                        transition: 'opacity 0.3s ease'
                      }}
                    />
                  </Link>
                </div>
              </div>
              <div className="col-xl-7 col-lg-6 d-none d-xl-block">
                <div className="main-menu menu-common-style menu-lh-1 menu-margin-4 menu-font-3 ml-20 menu-others-page">
                  <nav>
                    <ul>
                      <li className={location.pathname === '/' ? 'active' : ''}>
                        <Link to="/">{t.home}</Link>
                      </li>
                      <li className={location.pathname === '/products' || location.pathname.startsWith('/products/') ? 'active' : ''}>
                        <Link to="/products">{t.shop}</Link>
                      </li>
                      <li className={location.pathname === '/about' ? 'active' : ''}>
                        <Link to="/about">{t.about}</Link>
                      </li>
                      <li className={location.pathname === '/contact' ? 'active' : ''}>
                        <Link to="/contact">{t.contact}</Link>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
              <div className="col-xl-3 col-lg-3 col-md-6 col-6 position-relative">
                <div className="header-right-wrap mt-10">
                  <div className="language-menu-wrapper">
                    <button 
                      className={`language-nav-button ${languageMenuOpen ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setLanguageMenuOpen(!languageMenuOpen);
                      }}
                    >
                      {t.language}
                      <i className="la la-angle-down"></i>
                    </button>
                    {languageMenuOpen && (
                      <div className="language-dropdown">
                        <button
                          className={`lang-option ${language === 'en' ? 'active' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            changeLanguage('en');
                            setLanguageMenuOpen(false);
                          }}
                        >
                          English
                        </button>
                        <button
                          className={`lang-option ${language === 'ar' ? 'active' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            changeLanguage('ar');
                            setLanguageMenuOpen(false);
                          }}
                        >
                          العربية
                        </button>
                      </div>
                    )}
                  </div>
                  {isStandalone && !pushSubscribed && (
                    <button
                      className="pwa-push-btn"
                      onClick={async () => {
                        const sub = await subscribeToPush('client');
                        if (sub) setPushSubscribed(true);
                      }}
                      title={language === 'ar' ? 'تفعيل الإشعارات' : 'Enable Notifications'}
                    >
                      <i className="la la-bell"></i>
                    </button>
                  )}
                  <div className="cart-wrap common-style ml-35">
                    <Link to="/cart" className="cart-active cart-3">
                      <span className="mini-cart-price">{formatPriceEGP(cartTotal, language)}</span>
                      <i className="la la-shopping-cart"></i>
                      {cartItemCount > 0 && <span className="count-style">{formatNumber(cartItemCount, language)} {t.items}</span>}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile hamburger menu button - visible on mobile and iPad/tablet - positioned outside header */}
      <div className="mobile-off-canvas">
        <button className="mobile-aside-button" onClick={handleMobileMenuToggle} aria-label="Menu">
          <i className="la la-navicon"></i>
        </button>
      </div>
    
    {/* Mobile Side Navigation */}
    <div className={`mobile-off-canvas-active ${mobileMenuOpen ? 'inside' : ''}`}>
      {/* Mobile Sidebar Header */}
      <div className="mobile-sidebar-header">
        <Link to="/" onClick={handleMobileMenuClose} className="mobile-sidebar-logo">
          <img src="/assets/images/logo/logo-white.png" alt="Logo" loading="lazy" width="140" height="40" />
        </Link>
      </div>
      <div className="header-mobile-aside-wrap">
        {/* Navigation Menu */}
        <div className="mobile-menu-wrap">
          <nav>
            <ul className="mobile-menu">
              <li className={location.pathname === '/' ? 'active' : ''}>
                <Link to="/" onClick={handleMobileMenuClose}>
                  <i className="la la-home"></i>
                  <span>{t.home}</span>
                </Link>
              </li>
              <li className={location.pathname === '/products' || location.pathname.startsWith('/products/') ? 'active' : ''}>
                <Link to="/products" onClick={handleMobileMenuClose}>
                  <i className="la la-th-large"></i>
                  <span>{t.shop}</span>
                </Link>
              </li>
              <li className={location.pathname === '/about' ? 'active' : ''}>
                <Link to="/about" onClick={handleMobileMenuClose}>
                  <i className="la la-info-circle"></i>
                  <span>{t.about}</span>
                </Link>
              </li>
              <li className={location.pathname === '/contact' ? 'active' : ''}>
                <Link to="/contact" onClick={handleMobileMenuClose}>
                  <i className="la la-envelope"></i>
                  <span>{t.contact}</span>
                </Link>
              </li>
              <li className={location.pathname === '/cart' ? 'active' : ''}>
                <Link to="/cart" onClick={handleMobileMenuClose}>
                  <i className="la la-shopping-cart"></i>
                  <span>{t.cart}</span>
                </Link>
              </li>
              {!isStandalone && (
                <li className="install-app-item">
                  <a href="#install" onClick={(e) => { e.preventDefault(); handleInstallApp(); handleMobileMenuClose(); }}>
                    <i className="la la-download"></i>
                    <span>{language === 'ar' ? 'تحميل التطبيق' : 'Install App'}</span>
                  </a>
                </li>
              )}
            </ul>
          </nav>
        </div>

        {/* Language Selector */}
        <div className="mobile-language-section">
          <div className="mobile-language-wrapper">
            <div className="mobile-language-options">
              <button
                className={`mobile-lang-btn ${language === 'en' ? 'active' : ''}`}
                onClick={() => {
                  changeLanguage('en');
                  handleMobileMenuClose();
                }}
              >
                English
              </button>
              <button
                className={`mobile-lang-btn ${language === 'ar' ? 'active' : ''}`}
                onClick={() => {
                  changeLanguage('ar');
                  handleMobileMenuClose();
                }}
              >
                العربية
              </button>
            </div>
          </div>
        </div>

        {/* Contact Details Section */}
        <div className="mobile-contact-section">
          <div className="mobile-contact-title">
            {language === 'ar' ? 'بيانات التواصل' : 'Contact Details'}
          </div>
          {phoneNumber && (
            <div className="contact-item">
              <i className="la la-phone"></i>
              <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} dir="ltr">{formatPhoneNumber(phoneNumber, language)}</a>
            </div>
          )}
          {email && (
            <div className="contact-item contact-email">
              <i className="la la-envelope"></i>
              <a href={`mailto:${email}`} dir="ltr">{email}</a>
            </div>
          )}
          <div className="contact-item contact-address">
            <i className="la la-map-marker"></i>
            {locationMapUrl ? (
              <a href={locationMapUrl} target="_blank" rel="noopener noreferrer" onClick={handleMobileMenuClose}>
                {t.companyAddress}, {t.companyCityCountry}
              </a>
            ) : (
              <span>{t.companyAddress}, {t.companyCityCountry}</span>
            )}
          </div>
        </div>

        {/* Social Media Icons */}
        <div className="mobile-social-section">
          <div className="mobile-social-icons">
            {whatsappUrl && (
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="WhatsApp">
                <i className="la la-whatsapp"></i>
              </a>
            )}
            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                <i className="la la-instagram"></i>
              </a>
            )}
            {facebookUrl && (
              <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook">
                <i className="la la-facebook"></i>
              </a>
            )}
            {locationMapUrl && (
              <a href={locationMapUrl} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Location">
                <i className="la la-map-marker"></i>
              </a>
            )}
          </div>
        </div>

        {/* Credit Footer */}
        <div className="mobile-sidebar-footer">
          <div className="footer-line">{t.websiteDesignedBy || 'Website is designed and created by'}</div>
          <div className="footer-line">
            <a 
              href={creditUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              <strong>{language === 'ar' ? creditNameAr : creditName}</strong>
            </a>
          </div>
          <div className="footer-line" style={{ fontSize: '11px', opacity: 0.9, marginTop: '4px' }}>
            {t.taxNumber || 'Tax Number: 232446768'}
          </div>
        </div>
      </div>
    </div>
    
    {/* Body overlay when mobile menu is open */}
    {mobileMenuOpen && (
      <div className="body-overlay" onClick={handleMobileMenuClose}></div>
    )}

    {/* Install Guide Modal */}
    {showInstallGuide && (
      <>
        <div className="install-guide-overlay" onClick={() => setShowInstallGuide(false)}></div>
        <div className="install-guide-modal" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <button className="install-guide-close" onClick={() => setShowInstallGuide(false)}>
            <i className="la la-times"></i>
          </button>
          <div className="install-guide-header">
            <img src="/icons/icon-96x96.png" alt="App" className="install-guide-icon" loading="lazy" width="96" height="96" />
            <div>
              <h3>{language === 'ar' ? 'تحميل التطبيق' : 'Install App'}</h3>
              <p>Mohamed Hassan Amin</p>
            </div>
          </div>
          {isIOS ? (
            <div className="install-guide-steps">
              <div className="install-guide-step">
                <span className="install-guide-step-num">1</span>
                <span>
                  {language === 'ar' ? 'اضغط على زر ' : 'Tap the '}
                  <strong>{language === 'ar' ? 'المشاركة' : 'Share'}</strong>
                  {' '}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#007AFF" style={{verticalAlign: 'middle'}}><path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11a2 2 0 01-2 2H6a2 2 0 01-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3a2 2 0 012 2z"/></svg>
                  {language === 'ar' ? ' أسفل الشاشة' : ' at the bottom'}
                </span>
              </div>
              <div className="install-guide-step">
                <span className="install-guide-step-num">2</span>
                <span>
                  {language === 'ar' ? 'اضغط على ' : 'Tap '}
                  <strong>{language === 'ar' ? '"إضافة إلى الشاشة الرئيسية"' : '"Add to Home Screen"'}</strong>
                  {' '}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#007AFF" style={{verticalAlign: 'middle'}}><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                </span>
              </div>
              <div className="install-guide-step">
                <span className="install-guide-step-num">3</span>
                <span>
                  {language === 'ar' ? 'اضغط ' : 'Tap '}
                  <strong>{language === 'ar' ? '"إضافة"' : '"Add"'}</strong>
                </span>
              </div>
            </div>
          ) : (
            <div className="install-guide-steps">
              <div className="install-guide-step">
                <span className="install-guide-step-num">1</span>
                <span>
                  {language === 'ar' ? 'اضغط على ' : 'Tap '}
                  <strong>{language === 'ar' ? 'القائمة ⋮' : 'Menu ⋮'}</strong>
                  {language === 'ar' ? ' أعلى المتصفح' : ' in the browser toolbar'}
                </span>
              </div>
              <div className="install-guide-step">
                <span className="install-guide-step-num">2</span>
                <span>
                  {language === 'ar' ? 'اضغط على ' : 'Tap '}
                  <strong>{language === 'ar' ? '"إضافة إلى الشاشة الرئيسية"' : '"Add to Home Screen"'}</strong>
                  {' '} 
                  {language === 'ar' ? 'أو ' : 'or '}
                  <strong>{language === 'ar' ? '"تثبيت التطبيق"' : '"Install App"'}</strong>
                </span>
              </div>
            </div>
          )}
        </div>
      </>
    )}
    </>
  );
};

export default React.memo(Header);
